import { z } from 'zod';
import { isDecimalAmount } from './decimal-money';

/**
 * Übernahme eines Kassabuchs aus der abgelösten Base44-App (Monate + Buchungen).
 *
 * Arbeitsteilung: **Die Oberfläche liest die CSV im Browser** (wie
 * `materialImport.ts` / `blockedStockImportParse.ts`) und schickt fertig
 * strukturiertes JSON. Der Server parst KEIN CSV — er validiert, rechnet Geld
 * float-frei in Mikro-Einheiten um und schreibt idempotent.
 *
 * Geld steht hier als **Dezimalstring** („1832.95"), nicht als `number` (§6) und
 * nicht als `Money`: So kommt der Wert aus der Quelldatei, und die Umrechnung in
 * Mikro-Einheiten passiert genau einmal — serverseitig, mit
 * `decimalToMicroUnits`. Ein `number` im Transport wäre schon der Buchungsfehler.
 */

/** Ein Monatsdatensatz der Quelle (Anfangsbestand = gezählter Kassenstand am 1.). */
export const cashBookImportMonthSchema = z.object({
  // Dieselbe Grenze wie beim manuellen Anlegen (cash-book.schema.ts): Ein Monat
  // mit year 50 passierte sonst den Vertrag, wäre über die API aber nicht mehr
  // lesbar (dort gilt >= 2000) und ohne Monats-DELETE nur per SQL zu bereinigen.
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  /**
   * Dezimalstring wie „1832.95" — NIEMALS `number` (§6). Darf negativ sein
   * (Kassenstand im Minus ist fachlich möglich, wenn auch selten).
   */
  openingBalance: z
    .string()
    .refine(isDecimalAmount, 'Anfangsbestand muss ein Dezimalbetrag sein, z. B. "1832.95".'),
});
export type CashBookImportMonth = z.infer<typeof cashBookImportMonthSchema>;

/** Eine Buchung der Quelle. `sourceId` trägt die Idempotenz des ganzen Imports. */
export const cashBookImportEntrySchema = z.object({
  /**
   * Base44-Id der Buchung. Grundlage der deterministischen uuid des Zieldaten-
   * satzes: gleiche Quell-Id ⇒ gleiche uuid ⇒ ein zweiter Lauf trifft dieselbe
   * Zeile und legt nichts doppelt an. `CashBookEntry` hat KEIN natürliches
   * Unique, dies ist der einzige Anker.
   */
  sourceId: z.string().min(1),
  // Dieselbe Grenze wie beim manuellen Anlegen (cash-book.schema.ts): Ein Monat
  // mit year 50 passierte sonst den Vertrag, wäre über die API aber nicht mehr
  // lesbar (dort gilt >= 2000) und ohne Monats-DELETE nur per SQL zu bereinigen.
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  /** Buchungsdatum „2026-07-13" — muss in `year`/`month` fallen (prüft der Server). */
  entryDate: z.string().date(),
  kind: z.enum(['INCOME', 'EXPENSE']),
  /** Dezimalstring, positiv — die Richtung steckt in `kind`, nicht im Vorzeichen. */
  amount: z.string().refine(isDecimalAmount, 'Betrag muss ein Dezimalbetrag sein, z. B. "8.81".'),
  description: z.string().min(1),
  receiptNumber: z.string().optional(),
  note: z.string().optional(),
});
export type CashBookImportEntry = z.infer<typeof cashBookImportEntrySchema>;

/** Request-Body von `POST /v1/cash-book/import`. */
export const cashBookImportRequestSchema = z.object({
  months: z.array(cashBookImportMonthSchema),
  entries: z.array(cashBookImportEntrySchema),
});
export type CashBookImportRequest = z.infer<typeof cashBookImportRequestSchema>;

/**
 * Ergebnis eines Import-Laufs. „Skipped" heißt immer: **vorhanden geblieben**,
 * nie überschrieben — der Import legt ausschließlich neue Datensätze an.
 * `warnings` sind deutsche Klartext-Meldungen für die Oberfläche (z. B.
 * Kassenstand-Sprünge zwischen zwei Monaten); sie brechen den Import nicht ab.
 */
export const cashBookImportReportSchema = z.object({
  monthsCreated: z.number(),
  monthsSkipped: z.number(),
  entriesCreated: z.number(),
  entriesSkipped: z.number(),
  warnings: z.array(z.string()),
});
export type CashBookImportReport = z.infer<typeof cashBookImportReportSchema>;

/** Obergrenzen je Lauf — das ist ein Übernahme-Werkzeug, kein Massen-Ladeprogramm. */
export const CASH_BOOK_IMPORT_LIMITS = {
  months: 240,
  entries: 2000,
} as const;
